/**
 * Phase J.3.3 — `/admin/billing-schedules` list page.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Play, RefreshCw, Search, Repeat, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

import { DashboardShell } from "@/components/DashboardShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api, { formatApiError } from "@/lib/api";

function StatusPill({ status }) {
    const m = {
        active: ["Active", "bg-emerald-50 text-emerald-900 ring-emerald-200"],
        paused: ["Paused", "bg-amber-50 text-amber-900 ring-amber-200"],
        ended:  ["Ended",  "bg-slate-100 text-slate-700 ring-slate-200"],
    };
    const [label, cls] = m[status] || [status, "bg-slate-100 text-slate-700 ring-slate-200"];
    return (
        <span className={"inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ring-inset " + cls}>
            {label}
        </span>
    );
}


function FilterChip({ label, active, onClick, testId }) {
    return (
        <button
            type="button"
            data-testid={testId}
            onClick={onClick}
            className={
                "inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium transition-colors ring-1 ring-inset " +
                (active
                    ? "bg-blue-900 text-white ring-blue-900"
                    : "bg-white text-slate-700 ring-slate-200 hover:bg-slate-50")
            }
        >
            {label}
        </button>
    );
}


export default function AdminBillingSchedulesPage() {
    const location = useLocation();
    const urlParams = new URLSearchParams(location.search);
    const atRiskMode = urlParams.get("filter") === "at_risk";

    const [statusFilter, setStatusFilter] = useState("active,paused");
    const [q, setQ] = useState("");
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [running, setRunning] = useState(false);
    const [error, setError] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = atRiskMode
                ? { filter: "at_risk", limit: 50 }
                : { status: statusFilter, limit: 50 };
            if (q) params.q = q;
            const { data } = await api.get("/admin/billing-schedules", { params });
            setItems(data.items || []);
            setTotal(data.total || 0);
            setError(null);
        } catch (e) {
            setError(formatApiError(e));
        } finally {
            setLoading(false);
        }
    }, [statusFilter, q, atRiskMode]);

    useEffect(() => { load(); }, [load]);

    const handleRunNow = async () => {
        setRunning(true);
        try {
            const { data } = await api.post("/admin/crons/run-recurring-billing");
            const created = (data.created || []).length;
            const skipped = (data.skipped || []).length;
            const errs = (data.errors || []).length;
            toast.success(`Scheduler ran: ${created} issued · ${skipped} skipped · ${errs} errors.`);
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setRunning(false);
        }
    };

    return (
        <DashboardShell>
            <div data-testid="billing-schedules-page" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-0 py-6 space-y-5">
                <header className="flex items-start justify-between gap-4 flex-wrap">
                    <div>
                        <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-slate-500">Billing automation</p>
                        <h1 className="text-2xl font-bold tracking-tight text-slate-900">Recurring billing schedules</h1>
                        <p className="text-sm text-slate-500 mt-1">
                            {total === 0 ? "No schedules yet." : `${total} schedule${total === 1 ? "" : "s"} across all rentals.`}
                        </p>
                    </div>
                    <Button
                        type="button"
                        onClick={handleRunNow}
                        disabled={running}
                        data-testid="run-scheduler-now"
                        className="gap-1.5"
                    >
                        {running ? (
                            <><RefreshCw size={14} className="animate-spin" aria-hidden="true" />Running…</>
                        ) : (
                            <><Play size={14} aria-hidden="true" />Run scheduler now</>
                        )}
                    </Button>
                </header>

                <div className="flex flex-wrap items-center gap-2">
                    {atRiskMode && (
                        <Link
                            to="/admin/billing-schedules"
                            data-testid="filter-at-risk-chip"
                            className="inline-flex items-center gap-1.5 rounded-full bg-red-50 text-red-700 ring-1 ring-red-200 px-3 py-1 text-xs font-semibold hover:bg-red-100 transition-colors"
                            title="Clear at-risk filter"
                        >
                            <AlertTriangle size={12} /> At risk (auto-charge without PM) · clear
                        </Link>
                    )}
                    {!atRiskMode && (
                        <>
                            <FilterChip testId="filter-active" label="Active + Paused" active={statusFilter === "active,paused"} onClick={() => setStatusFilter("active,paused")} />
                    <FilterChip testId="filter-active-only" label="Active" active={statusFilter === "active"} onClick={() => setStatusFilter("active")} />
                    <FilterChip testId="filter-paused" label="Paused" active={statusFilter === "paused"} onClick={() => setStatusFilter("paused")} />
                    <FilterChip testId="filter-ended" label="Ended" active={statusFilter === "ended"} onClick={() => setStatusFilter("ended")} />
                    <FilterChip testId="filter-all" label="All" active={statusFilter === "active,paused,ended"} onClick={() => setStatusFilter("active,paused,ended")} />
                        </>
                    )}
                    <div className="ml-auto inline-flex items-center gap-2">
                        <Search size={14} className="text-slate-400" aria-hidden="true" />
                        <Input
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Search by unit number"
                            className="h-8 w-56"
                            data-testid="filter-search"
                        />
                    </div>
                </div>

                {error && (
                    <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">{error}</div>
                )}

                <section className="bg-white rounded-lg ring-1 ring-slate-200 overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
                            <tr>
                                <th className="text-left px-4 py-2 font-semibold">Rental</th>
                                <th className="text-left px-4 py-2 font-semibold">Customer</th>
                                <th className="text-left px-4 py-2 font-semibold">Cadence</th>
                                <th className="text-right px-4 py-2 font-semibold">Amount</th>
                                <th className="text-left px-4 py-2 font-semibold">Next run</th>
                                <th className="text-left px-4 py-2 font-semibold">Last run</th>
                                <th className="text-left px-4 py-2 font-semibold">Status</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {loading ? (
                                <tr><td colSpan={7} className="px-4 py-10 text-center text-sm text-slate-500">Loading…</td></tr>
                            ) : items.length === 0 ? (
                                <tr>
                                    <td colSpan={7} className="px-4 py-12 text-center">
                                        <Repeat size={24} className="mx-auto text-slate-300" aria-hidden="true" />
                                        <p className="text-sm text-slate-600 mt-2">No schedules match this filter.</p>
                                    </td>
                                </tr>
                            ) : (
                                items.map((s) => (
                                    <tr
                                        key={s.id}
                                        data-testid={`bs-row-${s.id}`}
                                        className="hover:bg-slate-50"
                                    >
                                        <td className="px-4 py-3 align-top">
                                            {s.rental ? (
                                                <Link to={`/admin/rentals/${s.rental.id}`} className="text-blue-700 hover:text-blue-900 font-medium">
                                                    {s.rental.unit_number || s.rental.id.slice(0, 8)}
                                                </Link>
                                            ) : "—"}
                                            <div className="text-[11px] text-slate-500">{s.rental?.equipment_type || ""}</div>
                                        </td>
                                        <td className="px-4 py-3 align-top">
                                            <div className="text-slate-900">{s.customer?.company_name || s.customer_org_id}</div>
                                            <div className="text-[11px] text-slate-500">{s.customer?.billing_contact_email || ""}</div>
                                        </td>
                                        <td className="px-4 py-3 align-top">{s.cadence_summary}</td>
                                        <td className="px-4 py-3 align-top text-right font-mono">
                                            ${(s.amount_cents / 100).toLocaleString("en-US", { minimumFractionDigits: 2 })}
                                        </td>
                                        <td className="px-4 py-3 align-top font-mono text-xs">{s.next_run_date || "—"}</td>
                                        <td className="px-4 py-3 align-top font-mono text-xs">{s.last_run_date || "—"}</td>
                                        <td className="px-4 py-3 align-top"><StatusPill status={s.status} /></td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </section>
            </div>
        </DashboardShell>
    );
}
